/**
 * Cart Discount Code Management
 * Single source of truth approach - all cart updates go through buildCart
 */

(function() {
  'use strict';

  // Helper function to format money
  function formatMoney(cents) {
    if (window.Shopify && window.Shopify.formatMoney && window.theme && window.theme.moneyFormat) {
      return window.Shopify.formatMoney(cents, window.theme.moneyFormat);
    }
    return '$' + (cents / 100).toFixed(2);
  }

  // Apply discount code using /cart/update.js with discount parameter
  async function applyDiscountCode(code, messageEl, buttonEl, inputEl) {
    if (!code || !code.trim()) {
      showMessage(messageEl, 'Please enter a discount code', 'error');
      return;
    }

    code = code.trim();

    // Show loading state
    const originalButtonText = buttonEl.textContent;
    buttonEl.disabled = true;
    buttonEl.textContent = 'Applying...';
    buttonEl.style.opacity = '0.6';
    showMessage(messageEl, 'Applying discount...', 'info');

    try {
      // Apply discount using /cart/update.js
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          discount: code
        })
      });

      if (!response.ok) {
        throw new Error('Failed to apply discount');
      }

      const cart = await response.json();

      // Check if discount was applied by comparing prices or checking discount applications
      const hasDiscount = (cart.total_price < cart.original_total_price) ||
                         (cart.cart_level_discount_applications && cart.cart_level_discount_applications.length > 0);


      if (hasDiscount) {
        showMessage(messageEl, '✓ Discount code applied successfully!', 'success');
        inputEl.value = '';
        closeDiscountBox();
      } else {
        showMessage(messageEl, 'Invalid discount code. Please try again.', 'error');
      }

      // Try multiple methods to rebuild cart
      if (window.theme && window.theme.cartForm && typeof window.theme.cartForm.buildCart === 'function') {
        window.theme.cartForm.buildCart();
      } else if (window.theme && window.theme.CartDrawer && window.theme.CartDrawer.prototype.cartForm) {
        window.theme.CartDrawer.prototype.cartForm.buildCart();
      } else {
        console.warn('⚠️ theme.cartForm not found! Using fallback methods...');

        // Fallback 1: Dispatch event
        document.dispatchEvent(new Event('cart:build'));

        // Fallback 2: Manually update discount display
        setTimeout(() => {
          updateDiscountDisplay(cart);
          updateCartTotals();
        }, 100);
      }
      updateCartTotals();

      buttonEl.disabled = false;
      buttonEl.textContent = originalButtonText;
      buttonEl.style.opacity = '1';

      setTimeout(() => {
        if (messageEl) messageEl.style.display = 'none';
      }, 3000);

    } catch (error) {
      console.error('Error applying discount:', error);
      showMessage(messageEl, 'Error applying discount code. Please try again.', 'error');
      buttonEl.disabled = false;
      buttonEl.textContent = originalButtonText;
      buttonEl.style.opacity = '1';
    }
  }

  // Update discount display with applied discounts
  function updateDiscountDisplay(cart) {

    const listElements = [
      document.getElementById('CartDiscountsList'),
      document.getElementById('CartPageDiscountsList')
    ];


    listElements.forEach(listEl => {
      if (!listEl) {
        return;
      }

      // Check if there are applied discounts
      if (!cart.cart_level_discount_applications || cart.cart_level_discount_applications.length === 0) {
        listEl.innerHTML = '';
        return;
      }

      let totalSavings = 0;
      let discountsHTML = '';

      // Show applied discounts with savings
      cart.cart_level_discount_applications.forEach(discount => {
        totalSavings += discount.total_allocated_amount;

        discountsHTML += `
          <div class="discount-badge" style="display: inline-flex; align-items: center; gap: 8px; padding: 8px 12px; background: #f0f9f4; border-radius: 20px; border: 1px solid #d4edda; margin-top: 8px; margin-right: 8px;">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#28a745" stroke-width="2">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
            <span style="font-size: 13px; font-weight: 600; color: #28a745;">${discount.title}</span>
            <span style="font-size: 12px; color: #666;">-${formatMoney(discount.total_allocated_amount)}</span>
            <button
              type="button"
              data-remove-discount="${discount.title}"
              class="discount-remove-btn"
              style="background: none; border: none; color: #dc3545; cursor: pointer; font-size: 18px; line-height: 1; padding: 0; margin-left: 4px; width: 20px; height: 20px; display: flex; align-items: center; justify-content: center; border-radius: 50%; transition: background 0.2s;"
              onmouseover="this.style.background='#ffe6e6'"
              onmouseout="this.style.background='none'"
              aria-label="Remove discount"
            >
              ×
            </button>
          </div>
        `;
      });

      // Wrap in savings banner
      if (totalSavings > 0) {
        discountsHTML = `
          <div style="margin-top: 12px; margin-bottom: 12px;">
            <div style="padding: 12px; background: #f0f9f4; border-radius: 8px; border: 1px solid #d4edda;">
              <div style="font-size: 15px; font-weight: 600; color: #28a745; margin-bottom: 10px;">
                🎉 You're saving ${formatMoney(totalSavings)}!
              </div>
              ${discountsHTML}
            </div>
          </div>
        `;
      }

      listEl.innerHTML = discountsHTML;
    });

    // Update line item discounts
    updateLineItemDiscounts(cart);
  }

  // Update line item discounts with strikethrough pricing
  function updateLineItemDiscounts(cart) {
    if (!cart.items) return;

    cart.items.forEach((item, index) => {
      const itemElement = document.querySelector(`[data-cart-item-index="${index}"]`);
      if (!itemElement) return;

      if (item.line_level_discount_allocations && item.line_level_discount_allocations.length > 0) {
        const priceElement = itemElement.querySelector('.cart-item__price');
        if (priceElement) {
          const originalPrice = item.original_line_price;
          const discountedPrice = item.final_line_price;

          priceElement.innerHTML = `
            <div style="display: flex; flex-direction: column; align-items: flex-end;">
              <span style="text-decoration: line-through; color: #999; font-size: 0.9em;">${formatMoney(originalPrice)}</span>
              <span style="color: #28a745; font-weight: 600;">${formatMoney(discountedPrice)}</span>
              <span style="font-size: 0.85em; color: #28a745;">Discount applied</span>
            </div>
          `;
        }
      }
    });
  }

  // Update cart totals without reload
  function updateCartTotals() {
    setTimeout(function() {
      fetch('/cart.js')
        .then(response => response.json())
        .then(cart => {
          if (!cart || typeof cart.original_total_price === 'undefined' || typeof cart.total_price === 'undefined') {
            return;
          }


          // Update subtotal
          const subtotalElements = document.querySelectorAll('[data-subtotal]');
          subtotalElements.forEach(el => {
            el.innerHTML = formatMoney(parseInt(cart.original_total_price, 10));
          });

          // Update final total
          const finalTotalElements = document.querySelectorAll('[data-final-total]');
          finalTotalElements.forEach(el => {
            el.innerHTML = formatMoney(parseInt(cart.total_price, 10));
          });
        });
    }, 50);
  }

  // Remove discount code
  async function removeDiscountCode(code) {

    try {
      // Clear discount using /cart/update.js
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({
          discount: ''
        })
      });

      if (!response.ok) {
        throw new Error('Failed to remove discount');
      }

      const cart = await response.json();

      // Rebuild cart drawer - this will fetch fresh data and update everything

      // Try multiple methods to rebuild cart
      if (window.theme && window.theme.cartForm && typeof window.theme.cartForm.buildCart === 'function') {
        window.theme.cartForm.buildCart();
      } else if (window.theme && window.theme.CartDrawer && window.theme.CartDrawer.prototype.cartForm) {
        window.theme.CartDrawer.prototype.cartForm.buildCart();
      } else {
        console.warn('⚠️ theme.cartForm not found! Using fallback methods...');

        // Fallback 1: Dispatch event
        document.dispatchEvent(new Event('cart:build'));

        // Fallback 2: Manually update discount display
        setTimeout(() => {
          updateDiscountDisplay(cart);
          updateCartTotals();
        }, 100);
      }
      updateCartTotals();

      // Show success message
      const messageEl = document.getElementById('CartDiscountMessage') ||
                       document.getElementById('CartPageDiscountMessage');
      if (messageEl) {
        showMessage(messageEl, 'Discount code removed', 'success');
        setTimeout(() => {
          messageEl.style.display = 'none';
        }, 2000);
      }

    } catch (error) {
      console.error('❌ Error removing discount:', error);

      // Show error message
      const messageEl = document.getElementById('CartDiscountMessage') ||
                       document.getElementById('CartPageDiscountMessage');
      if (messageEl) {
        showMessage(messageEl, 'Error removing discount. Please try again.', 'error');
      }
    }
  }

  // Close discount box
  function closeDiscountBox() {
    const box = document.querySelector('[data-discount-box]');
    if (box) {
      box.classList.remove('active');
    }
  }

  // Show message
  function showMessage(messageEl, text, type = 'info') {
    if (!messageEl) return;

    const colors = {
      success: '#28a745',
      error: '#dc3545',
      info: '#6c757d'
    };

    messageEl.textContent = text;
    messageEl.style.color = colors[type] || colors.info;
    messageEl.style.display = 'block';
  }


  // Initialize cart drawer discount
  function initCartDrawerDiscount() {
    const applyBtn = document.getElementById('CartDiscountApply');
    const input = document.getElementById('CartDiscountCode');
    const messageEl = document.getElementById('CartDiscountMessage');

    if (!applyBtn || !input) return;

    // Clone to remove old event listeners
    const newInput = input.cloneNode(true);
    newInput.value = input.value;
    input.parentNode.replaceChild(newInput, input);

    const newApplyBtn = applyBtn.cloneNode(true);
    applyBtn.parentNode.replaceChild(newApplyBtn, applyBtn);

    newApplyBtn.addEventListener('click', () => {
      const code = newInput.value;
      applyDiscountCode(code, messageEl, newApplyBtn, newInput);
    });

    newInput.addEventListener('keypress', (e) => {
      if (e.key === 'Enter') {
        newApplyBtn.click();
      }
    });

    // Cancel and close buttons
    const cancelBtn = document.querySelector('[data-discount-cancel]');
    if (cancelBtn) {
      const newCancelBtn = cancelBtn.cloneNode(true);
      cancelBtn.parentNode.replaceChild(newCancelBtn, cancelBtn);
      newCancelBtn.addEventListener('click', closeDiscountBox);
    }

    const closeBtn = document.querySelector('[data-close-discount]');
    if (closeBtn) {
      const newCloseBtn = closeBtn.cloneNode(true);
      closeBtn.parentNode.replaceChild(newCloseBtn, closeBtn);
      newCloseBtn.addEventListener('click', closeDiscountBox);
    }

    // Toggle button
    const toggleBtn = document.querySelector('[data-toggle-discount]');
    if (toggleBtn) {
      const newToggleBtn = toggleBtn.cloneNode(true);
      toggleBtn.parentNode.replaceChild(newToggleBtn, toggleBtn);

      newToggleBtn.addEventListener('click', function() {
        const box = document.querySelector('[data-discount-box]');
        if (box) {
          document.querySelectorAll('.add_note_box').forEach(b => {
            if (b !== box) b.classList.remove('active');
          });
          box.classList.toggle('active');
        }
      });
    }

    // Load existing discount
    loadExistingDiscount();
  }

  // Initialize cart page discount
  function initCartPageDiscount() {
    const applyBtn = document.getElementById('CartPageDiscountApply');
    const input = document.getElementById('CartPageDiscountCode');
    const messageEl = document.getElementById('CartPageDiscountMessage');

    if (!applyBtn || !input) return;

    const newInput = input.cloneNode(true);
    newInput.value = input.value;
    input.parentNode.replaceChild(newInput, input);

    const newApplyBtn = applyBtn.cloneNode(true);
    applyBtn.parentNode.replaceChild(newApplyBtn, applyBtn);

    newApplyBtn.addEventListener('click', () => {
      const code = newInput.value;
      applyDiscountCode(code, messageEl, newApplyBtn, newInput);
    });

    newInput.addEventListener('keypress', (e) => {
      if (e.key === 'Enter') {
        newApplyBtn.click();
      }
    });

    loadExistingDiscount();
  }

  // Load existing discount from cart
  async function loadExistingDiscount() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      updateDiscountDisplay(cart);
      updateCartTotals();
    } catch (error) {
      console.error('Error loading discount:', error);
    }
  }

  // Initialize discount remove buttons using event delegation
  function initDiscountRemoveButtons() {

    // Use event delegation on document to handle dynamically created buttons
    document.addEventListener('click', (e) => {
      // Check if clicked element or its parent has the remove discount attribute
      const removeBtn = e.target.closest('[data-remove-discount]') ||
                       e.target.closest('[data-remove-item-discount]') ||
                       e.target.closest('[data-discount-title]') ||
                       e.target.closest('.discount-remove-btn') ||
                       e.target.closest('.remove-item-discount-btn');

      if (removeBtn) {

        e.preventDefault();
        e.stopPropagation();

        // Try multiple attribute names for discount code
        const discountCode = removeBtn.getAttribute('data-remove-discount') ||
                            removeBtn.getAttribute('data-remove-item-discount') ||
                            removeBtn.getAttribute('data-discount-title') ||
                            removeBtn.getAttribute('data-discount-code');


        if (discountCode && discountCode.trim()) {
          removeDiscountCode(discountCode);
        } else {
          console.error('❌ No discount code found on button! Button HTML:', removeBtn.outerHTML);
        }
      }
    });
  }

  // Initialize - called after buildCart completes
  function init() {
    if (document.getElementById('CartDiscountApply')) {
      initCartDrawerDiscount();
    }

    if (document.getElementById('CartPageDiscountApply')) {
      initCartPageDiscount();
    }

    // Initialize remove button handler (only once)
    initDiscountRemoveButtons();

    // Listen for cart rebuild events - update display after rebuild
    document.addEventListener('cart:build', () => {
      setTimeout(() => {
        if (document.getElementById('CartDiscountApply')) {
          initCartDrawerDiscount();
        }
        loadExistingDiscount();
      }, 100);
    });

    // Listen for cart updated events from product card adds
    document.addEventListener('cart:updated', (e) => {
      if (e.detail) {
        updateDiscountDisplay(e.detail);
        updateCartTotals();
      }
    });

    // Listen for cart drawer refreshed events
    document.addEventListener('cart-drawer:refreshed', () => {
      setTimeout(() => {
        loadExistingDiscount();
      }, 100);
    });

    // Listen for product added events
    document.addEventListener('ajaxProduct:added', () => {
      setTimeout(() => {
        loadExistingDiscount();
      }, 200);
    });
  }

  // Hook into buildCart completion
  if (window.theme && window.theme.CartForm) {
    const originalBuildCart = window.theme.CartForm.prototype.buildCart;
    const originalCartMarkup = window.theme.CartForm.prototype.cartMarkup;

    // Override cartMarkup to update discounts after it completes
    window.theme.CartForm.prototype.cartMarkup = function(text) {

      // Call original
      originalCartMarkup.call(this, text);

      // After rebuild completes, fetch fresh cart and update discounts
      setTimeout(async () => {
        try {
          const response = await fetch('/cart.js');
          const cart = await response.json();
          updateDiscountDisplay(cart);
          updateCartTotals();
        } catch (error) {
          console.error('Error updating discount display after rebuild:', error);
        }
      }, 100);
    };
  }

  // Public API
  window.CartDiscount = {
    apply: applyDiscountCode,
    remove: removeDiscountCode,
    init: init,
    updateDisplay: updateDiscountDisplay
  };

  // Auto-initialize
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
